import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import MovieCard from "@/components/feature/MovieCard";
import { movieCategories, allMovies } from "@/mocks/movies";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || "action");
  const [sortBy, setSortBy] = useState<"rating" | "year" | "default">("default");

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = movieCategories.find((c) => c.slug === activeCategory);

  const filteredMovies = allMovies
    .filter((m) => m.categorySlug === activeCategory)
    .sort((a, b) => {
      if (sortBy === "rating") return b.rating - a.rating;
      if (sortBy === "year") return b.year - a.year;
      return 0;
    });

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors duration-200 cursor-pointer">
                首页
              </Link>
              <span>/</span>
              <span className="text-foreground-700 font-medium">
                {currentCategory?.name || "电影分类"}
              </span>
            </nav>

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                {currentCategory?.name || "电影分类"}
              </h1>
              <p className="text-sm text-foreground-500">
                天堂影视收录高清蓝光{currentCategory?.name || "电影"}，免广告在线观看，不缓冲不卡顿
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
              <aside className="lg:w-56 shrink-0">
                <div className="sticky top-24">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3">电影分类</h3>
                  <div className="space-y-1">
                    {movieCategories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.slug}`}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 cursor-pointer ${
                          activeCategory === cat.slug
                            ? "bg-primary-500/15 text-primary-600 font-medium"
                            : "text-foreground-500 hover:text-foreground-800 hover:bg-background-200/70"
                        }`}
                      >
                        <i className={`${cat.icon} text-base`}></i>
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                  <p className="text-sm text-foreground-500">
                    共找到 <strong className="text-foreground-900">{filteredMovies.length}</strong> 部电影
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-400 whitespace-nowrap">排序：</span>
                    <div className="flex bg-background-100 rounded-lg p-1">
                      {[
                        { value: "default" as const, label: "默认" },
                        { value: "rating" as const, label: "评分" },
                        { value: "year" as const, label: "年份" },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => setSortBy(opt.value)}
                          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                            sortBy === opt.value
                              ? "bg-background-50 text-foreground-900"
                              : "text-foreground-500 hover:text-foreground-700"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {filteredMovies.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                    {filteredMovies.map((movie) => (
                      <MovieCard
                        key={movie.id}
                        {...movie}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
                      <i className="ri-film-line text-2xl text-foreground-400"></i>
                    </div>
                    <p className="text-foreground-500">暂无该分类的电影</p>
                    <Link
                      to="/category/action"
                      className="inline-block mt-3 text-sm text-primary-600 hover:text-primary-500 transition-colors duration-200 cursor-pointer"
                    >
                      浏览其他分类
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: `天堂影视 - ${currentCategory?.name || "电影分类"}`,
            url: `https://tiantangmovies.com/category/${activeCategory}`,
            isPartOf: {
              "@type": "WebSite",
              name: "天堂影视",
              url: "https://tiantangmovies.com",
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "首页",
                  item: "https://tiantangmovies.com",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: currentCategory?.name || "电影分类",
                  item: `https://tiantangmovies.com/category/${activeCategory}`,
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}